import { PerformanceMetrics, MindfulnessReport, BreathingPattern } from '../types';

export function generateDynamicSomaticReflection(metrics: PerformanceMetrics): string {
  const duration = Math.max(1, metrics.durationSeconds || 10);
  const avgIntensity = metrics.averageIntensity || 0.3;
  const smoothness = metrics.smoothnessScore || 75;
  const tempoStability = metrics.tempoStability || 80;
  const jerkCount = metrics.jerkCount || 0;
  const totalNotes = metrics.totalNotesTriggered || 12;
  const presetName = metrics.presetName || 'Ambient';

  // 1. High fluidity + gentle movement (deep calm flow)
  if (smoothness >= 80 && avgIntensity <= 0.35) {
    return `Your fluid, low-resistance gestures (${smoothness.toFixed(0)}% smoothness) gently released upper-body tension, allowing your breath to settle into an effortless rhythm with "${presetName}".`;
  }

  // 2. Dynamic high-energy session with rapid gestures
  if (avgIntensity > 0.55 || totalNotes > 25) {
    return `High gesture velocity (${(avgIntensity * 100).toFixed(0)}% intensity across ${totalNotes} notes) actively engaged your motor reflexes, channeling physical energy into a dynamic rhythmic release.`;
  }

  // 3. Quick transitions / directional shifts
  if (jerkCount >= 3 || smoothness < 65) {
    return `Alternating between quick gestures and sudden stops (${jerkCount} shifts) engaged reactive motor control, highlighting an opportunity to soften wrist and shoulder tension.`;
  }

  // 4. High rhythmic cadence / tempo stability
  if (tempoStability >= 82) {
    return `Holding a steady tempo (${tempoStability.toFixed(0)}% consistency) created motor-sensory synchrony, physically stabilizing your posture and steadying your breath.`;
  }

  // 5. Extended deep immersion (> 55s)
  if (duration > 55) {
    return `Sustaining continuous movement over ${duration.toFixed(0)}s calmed your neuromuscular system into deep ease, progressively unwinding physical fatigue.`;
  }

  // 6. Quick micro-reset (< 15s)
  if (duration < 15) {
    return `A brief ${duration.toFixed(0)}s burst of intentional gestures stimulated circulation and provided an immediate physical reset from stationary screen focus.`;
  }

  // 7. Balanced harmonious flow
  return `Harmonizing ${smoothness.toFixed(0)}% gesture ease with ${tempoStability.toFixed(0)}% tempo stability aligned physical hand motions with centered breath awareness.`;
}

export function determineBreathingPattern(
  metrics: PerformanceMetrics,
  flowScore: number = 75,
  relaxScore: number = 75
): BreathingPattern {
  const duration = Math.max(1, metrics.durationSeconds || 10);
  const avgIntensity = metrics.averageIntensity || 0.3;
  const smoothness = metrics.smoothnessScore || 75;
  const tempoStability = metrics.tempoStability || 80;
  const jerkCount = metrics.jerkCount || 0;
  const totalNotes = metrics.totalNotesTriggered || 10;

  // 1. High-energy, rapid gestures, or sudden shifts -> 4-7-8 Relaxing Breath (Rapid parasympathetic down-regulation)
  if (avgIntensity > 0.48 || jerkCount >= 3 || totalNotes > 28) {
    return {
      id: 'calm-4-7-8',
      name: '4-7-8 Relaxing Breath',
      badge: '⚡ Rapid Down-Regulation (High Energy)',
      reason: `Matched for your dynamic ${avgIntensity > 0.48 ? `${(avgIntensity * 100).toFixed(0)}% movement intensity` : `${jerkCount} quick gesture shifts`}. The extended 8-second exhale stimulates the vagus nerve to ease pulse rate and release physical tension.`,
      inhale: 4,
      holdAfterInhale: 7,
      exhale: 8,
      holdAfterExhale: 0,
    };
  }

  // 2. Fragmented rhythm, uneven timing, or variable speed -> 4-4-4-4 Box Breathing (Cadence & Equilibrium)
  if (tempoStability < 68 || smoothness < 68 || (jerkCount >= 1 && smoothness < 75)) {
    return {
      id: 'box-4-4-4-4',
      name: 'Box Breathing (4-4-4-4)',
      badge: '🎯 Cadence & Equilibrium (Rhythm Reset)',
      reason: `Matched for your ${tempoStability.toFixed(0)}% tempo stability and reactive shifts. Symmetrical 4-count intervals restore autonomic balance and ground mental focus.`,
      inhale: 4,
      holdAfterInhale: 4,
      exhale: 4,
      holdAfterExhale: 4,
    };
  }

  // 3. Extended session with deep relaxation -> 4-4-8 Deep Restorative (Alpha-wave surrender)
  if (duration >= 45 && relaxScore >= 70) {
    return {
      id: 'restorative-4-4-8',
      name: '4-4-8 Deep Restorative',
      badge: '🌌 Deep Restorative (Extended Immersion)',
      reason: `Matched for your immersive ${duration.toFixed(0)}s session. A 4-second hold and deep 8-second release locks in calm neuromuscular stillness and eases fatigue.`,
      inhale: 4,
      holdAfterInhale: 4,
      exhale: 8,
      holdAfterExhale: 0,
    };
  }

  // 4. High fluidity and harmonic flow -> 5-5 Resonant Coherence (HRV flow synchronization)
  if (smoothness >= 78 && (flowScore >= 75 || avgIntensity <= 0.35)) {
    return {
      id: 'coherent-5-5',
      name: '5-5 Resonant Coherence',
      badge: '🌊 Harmonic Coherence (Fluid Flow)',
      reason: `Matched for your ${smoothness.toFixed(0)}% fluid motion. Symmetrical 5-second oceanic cycles optimize Heart Rate Variability (HRV) to sustain your effortless flow state.`,
      inhale: 5,
      holdAfterInhale: 0,
      exhale: 5,
      holdAfterExhale: 0,
    };
  }

  // 5. Balanced, gentle session -> 4-6 Gentle Calming (Default gentle baseline)
  return {
    id: 'extended-4-6',
    name: '4-6 Gentle Calming',
    badge: '🍃 Gentle Rest (Balanced Session)',
    reason: `Matched for your harmonious session. A gentle 4-second inhale with an unhurried 6-second exhale softly calms mental chatter without breath-holding effort.`,
    inhale: 4,
    holdAfterInhale: 0,
    exhale: 6,
    holdAfterExhale: 0,
  };
}

export async function analyzeMindfulnessSession(
  metrics: PerformanceMetrics,
  sessionName?: string
): Promise<MindfulnessReport> {
  try {
    const response = await fetch('/api/analyze-mindfulness', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        metrics,
        sessionName: sessionName || 'Motion Music Session',
      }),
    });

    if (!response.ok) {
      throw new Error(`Server returned HTTP ${response.status}`);
    }

    const data = await response.json();
    return data as MindfulnessReport;
  } catch (error) {
    console.warn('Network error or endpoint failure calling /api/analyze-mindfulness, generating local fallback:', error);

    const duration = Math.max(1, metrics.durationSeconds || 10);
    const avgIntensity = metrics.averageIntensity || 0.3;
    const smoothness = metrics.smoothnessScore || 75;
    const tempoStability = metrics.tempoStability || 80;
    const jerkCount = metrics.jerkCount || 0;
    const totalNotes = metrics.totalNotesTriggered || 12;
    const presetName = metrics.presetName || 'Ambient';

    let durationCategory: 'Micro Reset' | 'Flow State' | 'Deep Immersion' = 'Flow State';
    if (duration < 15) durationCategory = 'Micro Reset';
    else if (duration > 60) durationCategory = 'Deep Immersion';

    let relaxationLevel = 'Harmonic Calm';
    let flowScore = Math.round(72 + (smoothness * 0.2));
    let relaxScore = Math.round(68 + ((1 - avgIntensity) * 25));

    if (avgIntensity < 0.25 && jerkCount <= 2) {
      relaxationLevel = 'Deep Peaceful Rest';
      relaxScore = Math.min(98, relaxScore + 10);
    } else if (avgIntensity > 0.6) {
      relaxationLevel = 'Joyful Playful Energy';
      flowScore = Math.min(96, flowScore + 8);
    } else if (tempoStability > 85) {
      relaxationLevel = 'Centered & Grounded';
    }

    const finalFlowScore = Math.min(99, Math.max(50, flowScore));
    const finalRelaxScore = Math.min(99, Math.max(45, relaxScore));
    const breathingPattern = determineBreathingPattern(metrics, finalFlowScore, finalRelaxScore);

    // Generate unique takeaway based on movement metrics
    let uniqueTakeaway = `Take a moment to enjoy the quiet feeling right now. Let the gentle resonance of "${presetName}" remind you that you can always return to this centered presence with a single mindful breath.`;
    if (jerkCount > 2 || smoothness < 65) {
      uniqueTakeaway = `Notice where your shoulders or wrists might hold tension during quick transitions. Take a moment to roll your shoulders gently and let your hands feel light and unhurried.`;
    } else if (avgIntensity < 0.25) {
      uniqueTakeaway = `Your calm, feather-light gestures showed wonderful patience with "${presetName}". Carry that gentle, unhurried pace into your next conversation or keyboard break by pausing for one full, easy breath.`;
    } else if (avgIntensity > 0.6) {
      uniqueTakeaway = `You brought vibrant, lively energy to this session with ${totalNotes} notes played. To transition smoothly into resting mode, take two long sighing exhales to let that dynamic spark settle into peaceful focus.`;
    } else if (tempoStability > 85) {
      uniqueTakeaway = `Your sense of steady timing was remarkably clear and centered throughout these ${duration.toFixed(0)} seconds. See if you can notice that same rhythmic ease in your footsteps during your next walk.`;
    } else if (duration > 60) {
      uniqueTakeaway = `Giving yourself over a minute of continuous musical play is a wonderful gift to your mind. Notice the calm space you created, and gently check in with your breath whenever you feel hurried today.`;
    }

    const paceSummary = breathingPattern.holdAfterInhale > 0
      ? `${breathingPattern.inhale}s Inhale • ${breathingPattern.holdAfterInhale}s Hold • ${breathingPattern.exhale}s Exhale${breathingPattern.holdAfterExhale > 0 ? ` • ${breathingPattern.holdAfterExhale}s Hold` : ''} (${breathingPattern.name})`
      : `${breathingPattern.inhale}s Inhale • ${breathingPattern.exhale}s Exhale (${breathingPattern.name})`;

    return {
      flowStateScore: finalFlowScore,
      relaxationLevel,
      relaxationScore: finalRelaxScore,
      durationCategory,
      summary: `${smoothness > 70 ? 'Serene, natural flow' : 'Vibrant, creative spark'} with "${presetName}".`,
      mindfulnessHighlights: [
        `Maintained ${tempoStability.toFixed(0)}% steady cadence across ${totalNotes} notes.`,
        `${smoothness.toFixed(0)}% gesture fluidity with ${jerkCount === 0 ? 'relaxed ease' : `${jerkCount} dynamic shifts`}.`,
        `Warm resonance of "${presetName}" anchored calm present attention.`
      ],
      somaticReflection: generateDynamicSomaticReflection(metrics),
      recommendedBreathingPace: paceSummary,
      breathingPattern,
      wellnessAdvice: uniqueTakeaway,
      generatedAt: Date.now()
    };
  }
}
